import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faSearch, 
  faFilter, 
  faChevronDown, 
  faEye, 
  faEdit, 
  faClipboardCheck, 
  faCheckCircle,
  faExclamationTriangle,
  faTimesCircle,
  faDownload,
  faUpload,
  faCalendarAlt,
  faUser,
  faBuilding,
  faBell,
  faBell as faAlarm,
  faClock,
  faPlus,
  faXmark,
  faQuestionCircle
} from '@fortawesome/free-solid-svg-icons';
import './FireAlarms.css';
import { useParams } from 'react-router-dom';
import { getApiEndpoint } from '../../../../config/api';

const FireAlarms = () => {
  const { id: propertyId } = useParams();
  const [alarms, setAlarms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [showInspectionModal, setShowInspectionModal] = useState(false);
  const [selectedAlarm, setSelectedAlarm] = useState(null);
  const [viewMode, setViewMode] = useState('table'); // 'table' or 'card'
  const [filters, setFilters] = useState({
    property: '',
    systemType: '',
    status: '',
    dateRange: ''
  });
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [formData, setFormData] = useState({
    system_id: '',
    type: '',
    location: '',
    lastInspection: '',
    nextInspection: '',
    status: 'active',
    contractor: '',
    notes: ''
  });
  const [actionLoading, setActionLoading] = useState(false);
  const [actionError, setActionError] = useState(null);

  // Move fetchAlarms here so it's accessible everywhere
  const fetchAlarms = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        getApiEndpoint(`/api/properties/${propertyId}/fire-safety/fire-alarms`),
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      if (!response.ok) throw new Error('Failed to fetch fire alarm systems');
      const data = await response.json();
      setAlarms(
        data.map(alarm => ({
          id: alarm.id,
          system_id: alarm.system_id || '',
          type: alarm.type || '',
          location: alarm.location_description,
          lastInspection: alarm.last_inspection_date,
          nextInspection: alarm.next_inspection_date,
          status: alarm.status === 'Fail' ? 'faulty' : alarm.status === 'Pending' ? 'maintenance' : 'active',
          contractor: alarm.contractor || '',
          notes: alarm.notes,
        }))
      );
      setLoading(false);
    } catch (err) {
      setError('Failed to fetch fire alarm systems');
      setLoading(false);
    }
  };

  useEffect(() => {
    if (propertyId) fetchAlarms();
  }, [propertyId]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
  };

  const handleViewModeChange = (mode) => {
    setViewMode(mode);
  };

  const handleLogInspection = (alarm) => {
    setSelectedAlarm(alarm);
    setShowInspectionModal(true);
  };

  const handleCloseModal = () => {
    setShowInspectionModal(false);
    setSelectedAlarm(null);
  };

  const handleInspectionSubmit = (e) => {
    e.preventDefault();
    // Handle form submission
    // In a real app, this would make an API call to save the inspection
    console.log('Inspection logged for:', selectedAlarm.id);
    handleCloseModal();
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'active':
        return <span className="status-badge active">Active</span>;
      case 'maintenance':
        return <span className="status-badge maintenance">Needs Maintenance</span>;
      case 'faulty':
        return <span className="status-badge faulty">Faulty</span>;
      default:
        return <span className="status-badge unknown">Unknown</span>;
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'active':
        return <FontAwesomeIcon icon={faCheckCircle} className="status-icon active" />;
      case 'maintenance':
        return <FontAwesomeIcon icon={faExclamationTriangle} className="status-icon maintenance" />;
      case 'faulty':
        return <FontAwesomeIcon icon={faTimesCircle} className="status-icon faulty" />;
      default:
        return <FontAwesomeIcon icon={faQuestionCircle} className="status-icon unknown" />;
    }
  };

  const filteredAlarms = alarms.filter(alarm => {
    // Apply search filter
    if (
      searchQuery &&
      !String(alarm.system_id || '').toLowerCase().includes(searchQuery.toLowerCase()) &&
      !String(alarm.location || '').toLowerCase().includes(searchQuery.toLowerCase())
    ) {
      return false;
    }
    
    // Apply other filters
    if (filters.property && alarm.location.split(' - ')[0] !== filters.property) {
      return false;
    }
    
    if (filters.systemType && alarm.type !== filters.systemType) {
      return false;
    }
    
    if (filters.status && alarm.status !== filters.status) {
      return false;
    }
    
    // Date range filter would be implemented here
    
    return true;
  });

  // Add Fire Alarm
  const handleAddAlarm = async (e) => {
    e.preventDefault();
    setActionLoading(true);
    setActionError(null);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        getApiEndpoint(`/api/properties/${propertyId}/fire-safety/fire-alarms`),
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            system_id: formData.system_id,
            type: formData.type,
            location_description: formData.location,
            last_inspection_date: formData.lastInspection,
            next_inspection_date: formData.nextInspection,
            status: formData.status === 'faulty' ? 'Fail' : formData.status === 'maintenance' ? 'Pending' : 'Pass',
            contractor: formData.contractor,
            notes: formData.notes
          })
        }
      );
      if (!response.ok) throw new Error('Failed to add fire alarm');
      setShowAddModal(false);
      setFormData({ system_id: '', type: '', location: '', lastInspection: '', nextInspection: '', status: 'active', contractor: '', notes: '' });
      await fetchAlarms();
    } catch (err) {
      setActionError(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  // Edit Fire Alarm
  const handleEditAlarm = async (e) => {
    e.preventDefault();
    setActionLoading(true);
    setActionError(null);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        getApiEndpoint(`/api/properties/${propertyId}/fire-safety/fire-alarms/${selectedAlarm.id}`),
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            system_id: formData.system_id,
            type: formData.type,
            location_description: formData.location,
            last_inspection_date: formData.lastInspection,
            next_inspection_date: formData.nextInspection,
            status: formData.status === 'faulty' ? 'Fail' : formData.status === 'maintenance' ? 'Pending' : 'Pass',
            contractor: formData.contractor,
            notes: formData.notes
          })
        }
      );
      if (!response.ok) throw new Error('Failed to update fire alarm');
      setShowEditModal(false);
      setSelectedAlarm(null);
      setFormData({ system_id: '', type: '', location: '', lastInspection: '', nextInspection: '', status: 'active', contractor: '', notes: '' });
      await fetchAlarms();
    } catch (err) {
      setActionError(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  // Delete Fire Alarm
  const handleDeleteAlarm = async (alarmId) => {
    if (!window.confirm('Are you sure you want to delete this fire alarm?')) return;
    setActionLoading(true);
    setActionError(null);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        getApiEndpoint(`/api/properties/${propertyId}/fire-safety/fire-alarms/${alarmId}`),
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      if (!response.ok) throw new Error('Failed to delete fire alarm');
      // Refresh list
      await fetchAlarms();
    } catch (err) {
      setActionError(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  // Open Add Modal
  const openAddModal = () => {
    setFormData({ system_id: '', type: '', location: '', lastInspection: '', nextInspection: '', status: 'active', contractor: '', notes: '' });
    setShowAddModal(true);
  };

  // Open Edit Modal
  const openEditModal = (alarm) => {
    setSelectedAlarm(alarm);
    setFormData({
      system_id: alarm.system_id || '',
      type: alarm.type || '',
      location: alarm.location || '',
      lastInspection: alarm.lastInspection || '',
      nextInspection: alarm.nextInspection || '',
      status: alarm.status,
      contractor: alarm.contractor || '',
      notes: alarm.notes || ''
    });
    setShowEditModal(true);
  };

  if (loading) {
    return <div className="loading">Loading fire alarm systems...</div>;
  }

  if (error) {
    return <div className="error">{error}</div>;
  }

  return (
    <div className="fire-alarms-container">
      <div className="page-header">
        <div className="header-content">
          <h1>Fire Alarm Systems</h1>
          <p>Monitor and maintain the fire detection systems installed across your properties.</p>
        </div>
        <div className="header-actions">
          <button className="btn btn-primary" onClick={openAddModal}>
            <FontAwesomeIcon icon={faPlus} /> Add New System
          </button>
        </div>
      </div>

      <div className="search-filter-container">
        <div className="search-bar">
          <FontAwesomeIcon icon={faSearch} />
          <input
            type="text"
            placeholder="Search by ID, location, or notes..."
            value={searchQuery}
            onChange={handleSearch}
          />
        </div>
        
        <div className="filter-actions">
          <button 
            className={`view-mode-btn ${viewMode === 'table' ? 'active' : ''}`}
            onClick={() => handleViewModeChange('table')}
          >
            Table View
          </button>
          <button 
            className={`view-mode-btn ${viewMode === 'card' ? 'active' : ''}`}
            onClick={() => handleViewModeChange('card')}
          >
            Card View
          </button>
          <button 
            className="filter-btn"
            onClick={() => setShowFilters(!showFilters)}
          >
            <FontAwesomeIcon icon={faFilter} />
            Filters
          </button>
        </div>
      </div>

      {showFilters && (
        <div className="filters-panel">
          <div className="filter-group">
            <label>Property</label>
            <select 
              name="property" 
              value={filters.property} 
              onChange={handleFilterChange}
            >
              <option value="">All Properties</option>
              <option value="Main Building">Main Building</option>
              <option value="North Wing">North Wing</option>
              <option value="South Wing">South Wing</option>
              <option value="East Building">East Building</option>
              <option value="West Building">West Building</option>
            </select>
          </div>
          
          <div className="filter-group">
            <label>System Type</label>
            <select 
              name="systemType" 
              value={filters.systemType} 
              onChange={handleFilterChange}
            >
              <option value="">All Types</option>
              <option value="Addressable">Addressable</option>
              <option value="Conventional">Conventional</option>
              <option value="Wireless">Wireless</option>
            </select>
          </div>
          
          <div className="filter-group">
            <label>Status</label>
            <select 
              name="status" 
              value={filters.status} 
              onChange={handleFilterChange}
            >
              <option value="">All Statuses</option>
              <option value="active">Active</option>
              <option value="maintenance">Needs Maintenance</option>
              <option value="faulty">Faulty</option>
            </select>
          </div>
          
          <div className="filter-group">
            <label>Inspection Date</label>
            <select 
              name="dateRange" 
              value={filters.dateRange} 
              onChange={handleFilterChange}
            >
              <option value="">All Dates</option>
              <option value="last30">Last 30 Days</option>
              <option value="last90">Last 90 Days</option>
              <option value="last180">Last 180 Days</option>
              <option value="overdue">Overdue</option>
            </select>
          </div>
        </div>
      )}

      {viewMode === 'table' ? (
        <div className="table-container">
          <table className="alarms-table">
            <thead>
              <tr>
                <th>System ID</th>
                <th>Type</th>
                <th>Location</th>
                <th>Last Inspection</th>
                <th>Next Inspection</th>
                <th>Status</th>
                <th>Contractor</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredAlarms.length > 0 ? (
                filteredAlarms.map(alarm => (
                  <tr key={alarm.id}>
                    <td>{alarm.system_id}</td>
                    <td>{alarm.type}</td>
                    <td>{alarm.location}</td>
                    <td>{new Date(alarm.lastInspection).toLocaleDateString()}</td>
                    <td>{new Date(alarm.nextInspection).toLocaleDateString()}</td>
                    <td>{getStatusBadge(alarm.status)}</td>
                    <td>{alarm.contractor}</td>
                    <td className="actions-cell">
                      <button className="action-btn view" title="View Details">
                        <FontAwesomeIcon icon={faEye} />
                      </button>
                      <button className="action-btn edit" title="Edit System" onClick={() => openEditModal(alarm)}>
                        <FontAwesomeIcon icon={faEdit} />
                      </button>
                      <button 
                        className="action-btn inspect" 
                        title="Log Inspection"
                        onClick={() => handleLogInspection(alarm)}
                      >
                        <FontAwesomeIcon icon={faClipboardCheck} />
                      </button>
                      <button className="action-btn download" title="Download Report">
                        <FontAwesomeIcon icon={faDownload} />
                      </button>
                      <button className="action-btn delete" title="Delete" onClick={() => handleDeleteAlarm(alarm.id)}>
                        <FontAwesomeIcon icon={faTimesCircle} />
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="8" className="no-results">No fire alarm systems found matching your criteria</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="card-container">
          {filteredAlarms.length > 0 ? (
            filteredAlarms.map(alarm => (
              <div key={alarm.id} className="alarm-card">
                <div className="card-header">
                  <div className="card-title">
                    <h3>{alarm.system_id}</h3>
                    {getStatusIcon(alarm.status)}
                  </div>
                  <div className="card-actions">
                    <button className="action-btn view" title="View Details">
                      <FontAwesomeIcon icon={faEye} />
                    </button>
                    <button className="action-btn edit" title="Edit System" onClick={() => openEditModal(alarm)}>
                      <FontAwesomeIcon icon={faEdit} />
                    </button>
                    <button 
                      className="action-btn inspect" 
                      title="Log Inspection"
                      onClick={() => handleLogInspection(alarm)}
                    >
                      <FontAwesomeIcon icon={faClipboardCheck} />
                    </button>
                    <button className="action-btn delete" title="Delete" onClick={() => handleDeleteAlarm(alarm.id)}>
                      <FontAwesomeIcon icon={faTimesCircle} />
                    </button>
                  </div>
                </div>
                
                <div className="card-body">
                  <div className="card-info">
                    <div className="info-item">
                      <span className="info-label">Type:</span>
                      <span className="info-value">{alarm.type}</span>
                    </div>
                    <div className="info-item">
                      <span className="info-label">Location:</span>
                      <span className="info-value">{alarm.location}</span>
                    </div>
                    <div className="info-item">
                      <span className="info-label">Contractor:</span>
                      <span className="info-value">{alarm.contractor}</span>
                    </div>
                  </div>
                  
                  <div className="card-dates">
                    <div className="date-item">
                      <FontAwesomeIcon icon={faCalendarAlt} />
                      <span>Last Inspection: {new Date(alarm.lastInspection).toLocaleDateString()}</span>
                    </div>
                    <div className="date-item">
                      <FontAwesomeIcon icon={faClock} />
                      <span>Next Inspection: {new Date(alarm.nextInspection).toLocaleDateString()}</span>
                    </div>
                  </div>
                  
                  <div className="card-status">
                    {getStatusBadge(alarm.status)}
                  </div>
                  
                  {alarm.notes && (
                    <div className="card-notes">
                      <p>{alarm.notes}</p>
                    </div>
                  )}
                </div>
                
                <div className="card-footer">
                  <button className="btn btn-secondary">
                    <FontAwesomeIcon icon={faDownload} /> Download Report
                  </button>
                </div>
              </div>
            ))
          ) : (
            <div className="no-results">No fire alarm systems found matching your criteria</div>
          )}
        </div>
      )}

      {showInspectionModal && (
        <div className="modal-overlay">
          <div className="modal-container">
            <div className="modal-header">
              <h2>Log Inspection</h2>
              <button className="close-btn" onClick={handleCloseModal}>
                <FontAwesomeIcon icon={faXmark} />
              </button>
            </div>
            
            <div className="modal-body">
              <div className="inspection-info">
                <h3>{selectedAlarm.system_id} - {selectedAlarm.location}</h3>
                <p>Type: {selectedAlarm.type}</p>
              </div>
              
              <form onSubmit={handleInspectionSubmit}>
                <div className="form-group">
                  <label>Inspection Date</label>
                  <div className="input-with-icon">
                    <FontAwesomeIcon icon={faCalendarAlt} />
                    <input type="date" required />
                  </div>
                </div>
                
                <div className="form-group">
                  <label>Inspected By</label>
                  <div className="input-with-icon">
                    <FontAwesomeIcon icon={faUser} />
                    <select required>
                      <option value="">Select Inspector</option>
                      <option value="1">John Smith (Internal)</option>
                      <option value="2">Sarah Johnson (Internal)</option>
                      <option value="3">FireTech Solutions (External)</option>
                      <option value="4">SafetyFirst Systems (External)</option>
                    </select>
                  </div>
                </div>
                
                <div className="form-group">
                  <label>Status</label>
                  <select required>
                    <option value="active">Active</option>
                    <option value="maintenance">Needs Maintenance</option>
                    <option value="faulty">Faulty</option>
                  </select>
                </div>
                
                <div className="form-group">
                  <label>Notes</label>
                  <textarea rows="4" placeholder="Enter inspection notes..."></textarea>
                </div>
                
                <div className="form-group">
                  <label>Upload Files</label>
                  <div className="file-upload">
                    <button type="button" className="btn btn-secondary">
                      <FontAwesomeIcon icon={faUpload} /> Choose Files
                    </button>
                    <span className="file-hint">Upload images, PDF reports, or certificates</span>
                  </div>
                </div>
                
                <div className="form-actions">
                  <button type="button" className="btn btn-secondary" onClick={handleCloseModal}>
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary">
                    Save Inspection
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {showAddModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h2>Add Fire Alarm</h2>
            <form onSubmit={handleAddAlarm}>
              <div className="form-group">
                <label>System ID</label>
                <input type="text" value={formData.system_id} onChange={e => setFormData({ ...formData, system_id: e.target.value })} required />
              </div>
              <div className="form-group">
                <label>Type</label>
                <input type="text" value={formData.type} onChange={e => setFormData({ ...formData, type: e.target.value })} required />
              </div>
              <div className="form-group">
                <label>Location</label>
                <input type="text" value={formData.location} onChange={e => setFormData({ ...formData, location: e.target.value })} required />
              </div>
              <div className="form-group">
                <label>Last Inspection</label>
                <input type="date" value={formData.lastInspection} onChange={e => setFormData({ ...formData, lastInspection: e.target.value })} required />
              </div>
              <div className="form-group">
                <label>Next Inspection</label>
                <input type="date" value={formData.nextInspection} onChange={e => setFormData({ ...formData, nextInspection: e.target.value })} required />
              </div>
              <div className="form-group">
                <label>Status</label>
                <select value={formData.status} onChange={e => setFormData({ ...formData, status: e.target.value })} required>
                  <option value="active">Active</option>
                  <option value="maintenance">Needs Maintenance</option>
                  <option value="faulty">Faulty</option>
                </select>
              </div>
              <div className="form-group">
                <label>Contractor</label>
                <input type="text" value={formData.contractor} onChange={e => setFormData({ ...formData, contractor: e.target.value })} required />
              </div>
              <div className="form-group">
                <label>Notes</label>
                <textarea value={formData.notes} onChange={e => setFormData({ ...formData, notes: e.target.value })} />
              </div>
              {actionError && <div className="error">{actionError}</div>}
              <div className="modal-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setShowAddModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={actionLoading}>{actionLoading ? 'Adding...' : 'Add Fire Alarm'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
      {showEditModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h2>Edit Fire Alarm</h2>
            <form onSubmit={handleEditAlarm}>
              <div className="form-group">
                <label>System ID</label>
                <input type="text" value={formData.system_id} onChange={e => setFormData({ ...formData, system_id: e.target.value })} required />
              </div>
              <div className="form-group">
                <label>Type</label>
                <input type="text" value={formData.type} onChange={e => setFormData({ ...formData, type: e.target.value })} required />
              </div>
              <div className="form-group">
                <label>Location</label>
                <input type="text" value={formData.location} onChange={e => setFormData({ ...formData, location: e.target.value })} required />
              </div>
              <div className="form-group">
                <label>Last Inspection</label>
                <input type="date" value={formData.lastInspection} onChange={e => setFormData({ ...formData, lastInspection: e.target.value })} required />
              </div>
              <div className="form-group">
                <label>Next Inspection</label>
                <input type="date" value={formData.nextInspection} onChange={e => setFormData({ ...formData, nextInspection: e.target.value })} required />
              </div>
              <div className="form-group">
                <label>Status</label>
                <select value={formData.status} onChange={e => setFormData({ ...formData, status: e.target.value })} required>
                  <option value="active">Active</option>
                  <option value="maintenance">Needs Maintenance</option>
                  <option value="faulty">Faulty</option>
                </select>
              </div>
              <div className="form-group">
                <label>Contractor</label>
                <input type="text" value={formData.contractor} onChange={e => setFormData({ ...formData, contractor: e.target.value })} required />
              </div>
              <div className="form-group">
                <label>Notes</label>
                <textarea value={formData.notes} onChange={e => setFormData({ ...formData, notes: e.target.value })} />
              </div>
              {actionError && <div className="error">{actionError}</div>}
              <div className="modal-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setShowEditModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={actionLoading}>{actionLoading ? 'Saving...' : 'Save Changes'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default FireAlarms; 